import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Config, CONFIG_STORAGE_KEY } from '../lib/configManager.js';
import { safeStorage, safeJsonParse } from '../utils/storage.js';
import { sanitizeConfigOverride } from '../utils/security.js';

const AppConfigContext = createContext();

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(Config.config);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    Config.loadConfigOnce().then((loadedConfig) => {
      if (active) {
        setConfig(loadedConfig);
        setIsLoaded(true);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  const updateConfig = (nextConfig) => {
    const sanitizedConfig = sanitizeConfigOverride(nextConfig);
    const merged = Config.setConfig(sanitizedConfig);
    setConfig(merged);
    safeStorage.set(CONFIG_STORAGE_KEY, JSON.stringify(sanitizedConfig));
  };

  const resetConfig = async () => {
    safeStorage.remove(CONFIG_STORAGE_KEY);
    const merged = Config.setConfig({});
    setConfig(merged);

    const rawOverride = safeStorage.get(CONFIG_STORAGE_KEY);
    if (safeJsonParse(rawOverride)) {
      safeStorage.remove(CONFIG_STORAGE_KEY);
    }
  };

  const value = useMemo(() => ({
    config,
    isLoaded,
    updateConfig,
    resetConfig,
  }), [config, isLoaded]);

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return context;
};

export { CONFIG_STORAGE_KEY };
